import _ from 'lodash';

/**
 * 내부 캐시 구현 (일반 클래스 기반)
 */
class TrpcCacheStore {
    /**
     * 캐시 데이터
     * {
     *   [쿼리 키]: { data: 캐시 데이터, updatedAt: 캐시 데이터 업데이트 시간 }
     * }
     */
    private cache = new Map<string, { data: any; updatedAt: number }>();
    /**
     * 진행 중인 쿼리 요청을 대기하고 있는 프라미스
     * {
     *   [대기 쿼리 아이디]: 프라미스
     * }
     */
    private inflight = new Map<string, Promise<any>>();
    private defaultTTL = Infinity;

    get<T>(key: string, ttl = this.defaultTTL): T | null {
        const entry = this.cache.get(key);
        if (!entry) return null;
        if (Date.now() - entry.updatedAt > ttl) {
            this.cache.delete(key);
            return null;
        }
        return entry.data;
    }

    set<T>(key: string, data: T) {
        this.cache.set(key, { data, updatedAt: Date.now() });
    }

    invalidate(key: string) {
        this.cache.delete(key);
    }

    debug() {
        return {
            cache: Array.from(this.cache.entries()).map(([key, value]) => ({
                key,
                data: value.data,
                updatedAt: new Date(value.updatedAt).toISOString(),
            })),
            inflight: Array.from(this.inflight.keys()),
        };
    }

    private sortObjectKeys(obj: any): any {
        if (Array.isArray(obj)) {
            return obj.map((v) => this.sortObjectKeys(v));
        } else if (obj && typeof obj === 'object' && obj.constructor === Object) {
            return Object.keys(obj)
                .sort()
                .reduce((acc, key) => {
                    acc[key] = this.sortObjectKeys(obj[key]);
                    return acc;
                }, {} as any);
        } else {
            return obj;
        }
    }

    generateKey(path: string, input: any): string {
        try {
            const sorted = this.sortObjectKeys(input);
            return `${path}:${JSON.stringify(sorted)}`;
        } catch {
            return `${path}:<non-serializable>`;
        }
    }

    getInflight(key: string): Promise<any> | undefined {
        return this.inflight.get(key);
    }

    setInflight(key: string, promise: Promise<any>) {
        this.inflight.set(key, promise);
    }

    clearInflight(key: string) {
        this.inflight.delete(key);
    }
}

export const trpcCacheStore = typeof window !== 'undefined' ? new TrpcCacheStore() : undefined;

if (typeof window !== 'undefined' && trpcCacheStore) {
    (window as any).__trpcCacheDebug = trpcCacheStore;
}

function runCachedQuery(target: any, key: string, input: any): Promise<any> {
    const inflight = trpcCacheStore!.getInflight(key);
    if (inflight) return inflight;

    const promise = (async () => {
        try {
            const result = await target.query(input);
            trpcCacheStore!.set(key, result);
            return result;
        } finally {
            trpcCacheStore!.clearInflight(key);
        }
    })();

    trpcCacheStore!.setInflight(key, promise);
    return promise;
}

/**
 * trpcClient 확장 프록시 생성
 *
 * trcpClient 에 프록시로 $query, $forceQuery, $invalidate 확장 메서드 추가
 * - $query 는 캐시에 있는 데이터를 반환하고,
 * - $forceQuery 는 캐시에 있는 데이터를 무시하고 새로 조회
 * - $invalidate 는 캐시에 있는 데이터를 삭제
 * - $setCache 는 캐시 데이터를 직접 설정
 *
 * 디버깅
 * - window.__trpcCacheDebug 에 캐시 데이터와 진행 중인 쿼리 요청 목록 확인 가능
 */
export function createTrpcWithCache<T>(client: T, basePath: string[] = []): any {
    return new Proxy(client as any, {
        get(target, prop: string) {
            const nextPath = [...basePath, prop];
            const pathStr = basePath.join('.');

            if (prop === '$query') {
                return async (input: any = {}, opts?: { ttl?: number; force?: boolean }) => {
                    if (typeof target.query !== 'function') {
                        throw new Error(`Target at path "${pathStr}" does not support query()`);
                    }

                    if (!trpcCacheStore) {
                        return target.query(input);
                    }

                    const key = trpcCacheStore.generateKey(pathStr, input);
                    const cached = trpcCacheStore.get(key, opts?.ttl);
                    if (cached && !opts?.force) return cached;

                    return runCachedQuery(target, key, input);
                };
            }

            if (prop === '$forceQuery') {
                return async (input: any = {}) => {
                    if (typeof target.query !== 'function') {
                        throw new Error(`Target at path "${pathStr}" does not support query()`);
                    }

                    if (!trpcCacheStore) {
                        return target.query(input);
                    }

                    const key = trpcCacheStore.generateKey(pathStr, input);
                    return runCachedQuery(target, key, input);
                };
            }

            if (prop === '$invalidate') {
                return (input: any = {}) => {
                    if (!trpcCacheStore) return;
                    const key = trpcCacheStore.generateKey(pathStr, input);
                    trpcCacheStore.invalidate(key);
                };
            }

            if (prop === '$setCache') {
                return (input: any = {}, data: any) => {
                    if (!trpcCacheStore) return;
                    const key = trpcCacheStore.generateKey(pathStr, input);
                    trpcCacheStore.set(key, data);
                };
            }

            const original = target[prop];

            // mutate, subscribe 타입에 대해서는 캐시 확장 적용 안함
            if (prop === 'mutate' || prop === 'subscribe') {
                return original;
            }

            return createTrpcWithCache(original, nextPath);
        },
    });
}

/**
 * 타입 확장: 자동 완성을 위해 명시적으로 선언해주는 것도 가능 (선택)
 */
export type WithTrpcCache<T> = T extends object
    ? {
          // query 타입에 대해서만 확장
          [K in keyof T]: T[K] extends { query: (input: infer I) => Promise<infer R> }
              ? T[K] & {
                    $query: (input: I, opts?: { ttl?: number; force?: boolean }) => Promise<R>;
                    $forceQuery: (input: I) => Promise<R>;
                    $invalidate: (input: I) => void;
                    $setCache: (input: I, data: any) => void;
                }
              : T[K] extends { mutate: (input: infer I) => Promise<infer R> }
                ? T[K]
                : T[K] extends { subscribe: (input: infer I) => Promise<infer R> }
                  ? T[K]
                  : WithTrpcCache<T[K]>;
      }
    : T;

// 사용 예시:
// const cachedTrpcClient: WithTrpcCache<typeof trpcClient> = createTrpcWithCache(trpcClient);
// await cachedTrpcClient.user.getAll.$query(input);
